import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';

interface DeleteConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  itemName?: string;
  confirmText?: string;
  requiresTyping?: boolean;
}

export function DeleteConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  itemName,
  confirmText = 'Delete',
  requiresTyping = false,
}: DeleteConfirmationDialogProps) {
  const [typedValue, setTypedValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const canConfirm = requiresTyping && itemName
    ? typedValue === itemName
    : true;

  const handleConfirm = async () => {
    if (!canConfirm) return;

    setIsDeleting(true);
    // Small delay for better UX
    await new Promise((resolve) => setTimeout(resolve, 300));
    onConfirm();
    setIsDeleting(false);
    setTypedValue('');
  };

  const handleClose = () => {
    setTypedValue('');
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconAlertTriangle size={20} color="red" />
          <Text fw={600}>{title}</Text>
        </Group>
      }
      centered
      size="md"
      zIndex={10000}
    >
      <Stack gap="md">
        <Text size="sm" c="gray.6">
          {description}
        </Text>

        {requiresTyping && itemName && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Type <Text span fw={700} ff="monospace" c="red">
                {itemName}
              </Text> to confirm
            </Text>
            <TextInput
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={`Type "${itemName}" here`}
              autoFocus
            />
          </Stack>
        )}

        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="This action cannot be undone"
          color="yellow"
        >
          <Text size="xs">
            All data associated with this item will be permanently removed from our servers.
          </Text>
        </Alert>

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleConfirm}
            disabled={!canConfirm || isDeleting}
            loading={isDeleting}
          >
            {confirmText}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
